import type {
	FieldFilter,
	FieldFilterOperator,
	Filter,
	FilterRelationalQuantifier,
	LogicalFilter,
	LogicalFilterAND,
	LogicalFilterOR,
	UnaryFilterNot,
} from '../../types/filters';
import type { AnyItem } from '../../types/schema/item';
import { isObject } from '@vueuse/core';
import { Decimal } from 'decimal.js-light';
import isEqual from 'fast-deep-equal';
import { isNil } from '#layers/base';
import { get } from '#layers/base/utils/object';
import {
	FILTER_OPERATORS,
	FILTER_RELATIONAL_QUANTIFIERS,
	FilterOperator,
	LogicalOperator,
	UnaryFilterOperator,
} from '../../types/filters';

function isNumericString(value: unknown): value is string {
	if (typeof value !== 'string' || value.trim() === '') return false;

	try {
		// eslint-disable-next-line no-new
		new Decimal(value);
		return true;
	}
	catch {
		return false;
	}
}

function isNumericValue(value: unknown): value is number | string {
	if (typeof value === 'number') return !Number.isNaN(value);
	return isNumericString(value);
}

/**
 * Compares two numeric values.
 * Fast path: native comparison when both are numbers.
 * Slow path: Decimal.js when strings involved (preserves precision).
 */
function compareNumeric(a: number | string, b: number | string): number {
	if (typeof a === 'number' && typeof b === 'number') {
		return a < b ? -1 : (a > b ? 1 : 0);
	}

	return new Decimal(a).cmp(new Decimal(b));
}

type ItemShape = AnyItem;

export function isLogicalFilterAnd<TItem extends ItemShape>(filter?: Filter<TItem> | null): filter is LogicalFilterAND<TItem> {
	return !!filter && LogicalOperator.AND in filter && Array.isArray(filter._and);
}

export function isLogicalFilterOr<TItem extends ItemShape>(filter?: Filter<TItem> | null): filter is LogicalFilterOR<TItem> {
	return !!filter && LogicalOperator.OR in filter && Array.isArray(filter._or);
}

export function isUnaryFilterNot<TItem extends ItemShape>(filter?: Filter<TItem> | null): filter is UnaryFilterNot<TItem> {
	return !!filter && UnaryFilterOperator.NOT in filter;
}

export function isLogicalFilter<TItem extends ItemShape>(filter?: Filter<TItem> | null): filter is LogicalFilter<TItem> {
	return isLogicalFilterAnd(filter) || isLogicalFilterOr(filter);
}

export function isFieldFilter<TItem extends ItemShape>(filter?: Filter | null): filter is FieldFilter<TItem> {
	return !isLogicalFilter(filter) && !isUnaryFilterNot(filter);
}

export function isEmptyFilter(filter?: Filter | null) {
	if (isNil(filter)) {
		return true;
	}
	else if (isLogicalFilterAnd(filter)) {
		return filter._and.length === 0;
	}
	else if (isLogicalFilterOr(filter)) {
		return filter._or.length === 0;
	}
	else if (isUnaryFilterNot(filter)) {
		return isEmptyFilter(filter._not);
	}
	else {
		return Object.keys(filter).length === 0;
	}
}

export function isNonEmptyFilter(filter?: Filter | null): filter is Filter {
	return !isEmptyFilter(filter);
}

export function isFilterOperator(operator: string): operator is FilterOperator {
	return FILTER_OPERATORS.includes(operator as FilterOperator);
}

export function isFilterRelationalQuantifier(key: string): key is FilterRelationalQuantifier {
	return FILTER_RELATIONAL_QUANTIFIERS.includes(key as FilterRelationalQuantifier);
}

export function isFieldFilterOperator(obj: FieldFilter | FieldFilterOperator): obj is FieldFilterOperator {
	return Object.keys(obj).some((key) => isFilterOperator(key));
}

/**
 * Extracts primitive (non-relational) field names referenced in a filter tree.
 * Walks logical operators (_and, _or, _not) recursively and collects
 * field keys from field filters whose values are direct operator expressions.
 */
export function extractPrimitiveFilterFieldNames(filter?: Filter | null): string[] {
	const names = new Set<string>();
	collectPrimitiveFilterFieldNames(filter, names);
	return [...names];
}

function collectPrimitiveFilterFieldNames(filter: Filter | null | undefined, names: Set<string>) {
	if (!filter || typeof filter !== 'object') return;

	if (isLogicalFilterAnd(filter)) {
		for (const f of filter._and) {
			collectPrimitiveFilterFieldNames(f, names);
		}
	}
	else if (isLogicalFilterOr(filter)) {
		for (const f of filter._or) {
			collectPrimitiveFilterFieldNames(f, names);
		}
	}
	else if (isUnaryFilterNot(filter)) {
		collectPrimitiveFilterFieldNames(filter._not, names);
	}
	else if (isFieldFilter(filter)) {
		for (const key of Object.keys(filter)) {
			const value = filter[key];

			if (!value || typeof value !== 'object') {
				// Implicit equality (e.g. { status: 'active' }) - primitive
				names.add(key);
				continue;
			}

			const keys = Object.keys(value);

			if (keys.some((k) => isFilterRelationalQuantifier(k))) {
				continue; // O2M quantifier (_some, _every, _none)
			}

			if (isFieldFilterOperator(value as FieldFilter)) {
				names.add(key); // Direct operator expression - primitive
			}

			// Otherwise it's an M2O traversal (nested field object) - skip
		}
	}
}

/**
 * Checks if a filter contains any relational traversal (M2O or O2M).
 *
 * Relational filters are detected by:
 * - O2M: Contains a quantifier (_some, _every, _none)
 * - M2O: Field nesting depth > 1 before hitting an operator
 *
 * Non-relational filters have the operator directly after the field name,
 * e.g., `{ status: { _eq: 'active' } }` or `{ jsonField: { _eq: { nested: 'value' } } }`
 */
export function isRelationalFilter(filter?: Filter | null): boolean {
	if (!filter) return false;

	if (isLogicalFilterAnd(filter)) {
		return filter._and.some((f) => isRelationalFilter(f));
	}

	if (isLogicalFilterOr(filter)) {
		return filter._or.some((f) => isRelationalFilter(f));
	}

	if (isUnaryFilterNot(filter)) {
		return isRelationalFilter(filter._not);
	}

	// Field filter - check for relational patterns
	return containsRelationalTraversal(filter);
}

function containsRelationalTraversal(filter: FieldFilter): boolean {
	for (const fieldKey in filter) {
		const fieldValue = filter[fieldKey];

		if (!fieldValue || typeof fieldValue !== 'object') continue;

		// Check for O2M quantifiers
		const keys = Object.keys(fieldValue);

		if (keys.some((k) => isFilterRelationalQuantifier(k))) {
			return true;
		}

		// Check for M2O: if the value is not an operator, we're traversing a relation
		if (!isFieldFilterOperator(fieldValue as FieldFilter)) {
			return true;
		}
	}

	return false;
}

export function getFirstFieldPath(filter: FieldFilter) {
	const fieldPath: string[] = [];

	do {
		const key = Object.keys(filter)[0];

		if (key && !isFilterOperator(key)) {
			fieldPath.push(key);
			filter = filter[key] as FieldFilter;
		}
		else {
			return fieldPath;
		}
	} while (typeof filter === 'object');

	return [];
}

/**
 * Extracts the first filter operator from a (nested) field filter.
 * This will return unexpected results if the filter contains multiple fields.
 */
export function getFieldFilterOperator(filter: FieldFilter) {
	const fieldPath = getFirstFieldPath(filter);
	const filterOperator = get(filter, fieldPath);

	if (!filterOperator) return;
	return filterOperator;
}

export function getFieldFilterOperatorName(filter: FieldFilter): FilterOperator | undefined {
	return Object.keys(getFieldFilterOperator(filter) ?? {})[0] as FilterOperator | undefined;
}

export function getFieldFilterOperatorValue(filter: FieldFilter): any {
	const filterOperator = getFieldFilterOperator(filter);
	if (!filterOperator) return;
	const operatorName = Object.keys(filterOperator)[0] as FilterOperator;
	return filterOperator[operatorName];
}

export function createFieldFilter(fieldPath: string[], operator: FilterOperator, value?: any): FieldFilter {
	return fieldPath.reduceRight((acc, field) => ({
		[field]: acc,
	}), { [operator]: value });
}

export function matchesFilter<T>(item: T, filter: Filter): boolean {
	if (isEmptyFilter(filter)) {
		return true;
	}
	else if (isLogicalFilterAnd(filter) && filter._and.every((f) => matchesFilter(item, f))) {
		return true;
	}
	else if (isLogicalFilterOr(filter) && filter._or.some((f) => matchesFilter(item, f))) {
		return true;
	}
	else if (isUnaryFilterNot(filter)) {
		return !matchesFilter(item, filter._not);
	}
	else if (isFieldFilter(filter)) {
		return matchesFieldFilter(item, filter);
	}

	return false;
}

export function matchesFieldFilter<T>(item: T, filter: FieldFilter): boolean {
	for (const fieldKey in filter) {
		const keyValue = filter[fieldKey];
		const itemValue = item[fieldKey as keyof T];

		if (isObject(keyValue)) {
			const operators = Object.keys(keyValue);

			for (const operator of operators) {
				if (!operator) {
					throw new Error(`Missing operator for field ${fieldKey}`);
				}

				if (isFilterOperator(operator)) {
					const filterValue = (keyValue as FieldFilterOperator)[operator];

					// stop iterating over operators if we found a non-match
					if (!matchesOperator(itemValue, filterValue, operator)) {
						return false;
					}
				}
				else {
					console.error(`Unsupported nested field filter for field ${fieldKey}`);
					return false;
				}
			}
		}
		else {
			// special handling for implicit equal
			return matchesOperator(itemValue, keyValue, FilterOperator.EQ);
		}
	}

	return true;
}

export function matchesOperator(itemValue: any, filterValue: any, operator: FilterOperator): boolean {
	switch (operator) {
		case FilterOperator.EQ:
			if (typeof filterValue === 'object') {
				return isEqual(filterValue, itemValue);
			}

			return itemValue === filterValue;

		case FilterOperator.NEQ:
			return itemValue !== filterValue;

		case FilterOperator.CONTAINS:
			return typeof itemValue === 'string'
				&& typeof filterValue === 'string'
				&& itemValue.includes(filterValue);

		case FilterOperator.NCONTAINS:
			return typeof itemValue === 'string'
				&& typeof filterValue === 'string'
				&& !itemValue.includes(filterValue);

		case FilterOperator.ICONTAINS:
			return typeof itemValue === 'string'
				&& typeof filterValue === 'string'
				&& itemValue.toLowerCase().includes(filterValue.toLowerCase());

		case FilterOperator.NICONTAINS:
			return typeof itemValue === 'string'
				&& typeof filterValue === 'string'
				&& !itemValue.toLowerCase().includes(filterValue.toLowerCase());

		case FilterOperator.STARTS_WITH:
			return typeof itemValue === 'string'
				&& typeof filterValue === 'string'
				&& itemValue.startsWith(filterValue);

		case FilterOperator.NSTARTS_WITH:
			return typeof itemValue === 'string'
				&& typeof filterValue === 'string'
				&& !itemValue.startsWith(filterValue);

		case FilterOperator.ENDS_WITH:
			return typeof itemValue === 'string'
				&& typeof filterValue === 'string'
				&& itemValue.endsWith(filterValue);

		case FilterOperator.NENDS_WITH:
			return typeof itemValue === 'string'
				&& typeof filterValue === 'string'
				&& !itemValue.endsWith(filterValue);

		case FilterOperator.LT:
			if (isNumericValue(itemValue) && isNumericValue(filterValue)) {
				return compareNumeric(itemValue, filterValue) < 0;
			}

			if (itemValue == null || filterValue == null) return false;
			return String(itemValue) < String(filterValue);

		case FilterOperator.LTE:
			if (isNumericValue(itemValue) && isNumericValue(filterValue)) {
				return compareNumeric(itemValue, filterValue) <= 0;
			}

			if (itemValue == null || filterValue == null) return false;
			return String(itemValue) <= String(filterValue);

		case FilterOperator.GT:
			if (isNumericValue(itemValue) && isNumericValue(filterValue)) {
				return compareNumeric(itemValue, filterValue) > 0;
			}

			if (itemValue == null || filterValue == null) return false;
			return String(itemValue) > String(filterValue);

		case FilterOperator.GTE:
			if (isNumericValue(itemValue) && isNumericValue(filterValue)) {
				return compareNumeric(itemValue, filterValue) >= 0;
			}

			if (itemValue == null || filterValue == null) return false;
			return String(itemValue) >= String(filterValue);

		case FilterOperator.BETWEEN:
			return Array.isArray(filterValue)
				&& filterValue.length === 2
				&& isNumericValue(itemValue)
				&& isNumericValue(filterValue[0])
				&& isNumericValue(filterValue[1])
				&& compareNumeric(itemValue, filterValue[0]) >= 0
				&& compareNumeric(itemValue, filterValue[1]) <= 0;

		case FilterOperator.NBETWEEN:
			return Array.isArray(filterValue)
				&& filterValue.length === 2
				&& isNumericValue(itemValue)
				&& isNumericValue(filterValue[0])
				&& isNumericValue(filterValue[1])
				&& (compareNumeric(itemValue, filterValue[0]) < 0 || compareNumeric(itemValue, filterValue[1]) > 0);

		case FilterOperator.IN:
			if (!Array.isArray(filterValue)) return false;

			if (isNumericValue(itemValue)) {
				return filterValue.some((v) => isNumericValue(v) && compareNumeric(itemValue, v) === 0);
			}

			return typeof itemValue === 'string' && filterValue.includes(itemValue);

		case FilterOperator.NIN:
			if (!Array.isArray(filterValue)) return false;

			if (isNumericValue(itemValue)) {
				return !filterValue.some((v) => isNumericValue(v) && compareNumeric(itemValue, v) === 0);
			}

			return typeof itemValue === 'string' && !filterValue.includes(itemValue);

		case FilterOperator.NULL:
			if (filterValue === true) return isNil(itemValue);
			if (filterValue === false) return !isNil(itemValue);
			return false;

		default:
			console.error(`Unknown operator ${operator}`);
			return false;
	}
}

export function filterItems<T extends AnyItem>(items: T[], filter: Filter<T>): T[] {
	if (isEmptyFilter(filter)) {
		return items;
	}

	return items.filter((item) => matchesFilter(item, filter));
}

export function findItem<T extends AnyItem>(items: T[], filter: Filter<T>): T | undefined {
	if (isEmptyFilter(filter)) {
		return undefined;
	}

	return items.find((item) => matchesFilter(item, filter));
}
